import { Avatar, TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import { KWELLO_ICON, KwelloSvgIcon } from '../icon/KwelloSvgIcon';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { selectFlyoutData, toggleFlyout } from '../../store/slices/flyout.slice';
import { Typography } from '@progress/kendo-react-common';
import {
  useGetOverviewAlignmentsQuery,
  useGetOverviewInsightsQuery,
  useGetOverviewInteractionsQuery,
  useGetOverviewQuery,
} from '../../store/api/quickviews.api';
import { Spinner } from '../loader/Spinner';
import { differenceInDays } from 'date-fns';
import { DATASET_INITIAL_ID, DATE_FORMAT, TEAM_MEMBER_SESSION_KEY } from '../../constants';
import AlignmentTab from './AlignmentTab';
import InsightsTab from './InsightsTab';
import InteractionTab from './InteractionTab';
import { useMemo, useState, useEffect } from 'react';
import { useCompareUrlWithProjectId, useNavigateWithProjectId } from '../../hooks/route';
import { DropDownList } from '@progress/kendo-react-dropdowns';

import { Button } from '@progress/kendo-react-buttons';

import { applyCommonFilter } from '../../store/slices/common-filters.slice';
import { addChartFilter } from '../../store/slices/chart-filters.slice';
import { FilterParent } from '../../models/filter.model';
import { formatAppDate, generateAvatarInitials, setSessionItem } from '../../hooks/utils';
import useClaims from 'common/hooks/useClaims';
import { QuickViewProfileOverview } from 'common/models/quick-view.model';
import { FlyoutBody, FlyoutFooter } from './Flyout';
import { Claim } from 'common/models/page-config.models';
import FlyoutDropDownItem from './FlyoutDropDownItem';
import { FlyoutHeader } from './components/FlyoutHeader';
import {
  ALIGNMENT_ROUTES,
  INSIGHTS_ROUTES,
  INTERACTIONS_ROUTES,
  COACH_ROUTES,
} from '../../../routes/routes.const';
import useNavigationMenus from 'common/hooks/useMenus';

const INSIGHTS_URL = INSIGHTS_ROUTES.root;
const INTERRACTION_URL = INTERACTIONS_ROUTES.root;
const ALIGNMENT_URL = ALIGNMENT_ROUTES.root;

export interface LatestActivity {
  fieldTeam: string;
  latestDate: Date | null;
  territory: string;
}

export function ProfileQuickView() {
  const appNavigate = useNavigateWithProjectId();
  const { hasClaim } = useClaims();
  const dispatch = useAppDispatch();
  const flyoutParameters = useAppSelector(selectFlyoutData);
  const [sort, setSort] = useState<string>('Most Recent');
  const queryParameters = {
    oldestFirst: sort !== 'Most Recent',
    ...(!flyoutParameters.isTeamMember
      ? {
          profileId: flyoutParameters.profileId,
        }
      : {
          fieldTeamId: flyoutParameters.profileId,
          territoryId: flyoutParameters.territoryId,
        }),
  };

  const { data, isLoading, error } = useGetOverviewQuery(queryParameters);

  const hasAlignment = hasClaim(Claim.Alignment);
  const hasInsights = hasClaim(Claim.Insights);
  const hasInteraction = hasClaim(Claim.Interaction);
  const alignmentData = useGetOverviewAlignmentsQuery({
    ...queryParameters,
    enabled: hasAlignment,
  });
  const insightsData = useGetOverviewInsightsQuery({
    ...queryParameters,
    enabled: hasInsights,
  });
  const interactionData = useGetOverviewInteractionsQuery({
    ...queryParameters,
    enabled: hasInteraction,
  });
  const { compareUrl } = useCompareUrlWithProjectId();

  // Use compareUrl for page detection
  const isCoachPage = compareUrl(COACH_ROUTES.detail);
  const isAlignmentPage = compareUrl(ALIGNMENT_ROUTES.detail);
  const isInsightsPage = compareUrl(INSIGHTS_ROUTES.detail);
  const isInteractionsPage = compareUrl(INTERACTIONS_ROUTES.root);
  const { hasTeamDetailPage } = useNavigationMenus();
  const currentPage = useMemo(() => {
    if (isInteractionsPage) return 'interaction';
    if (isCoachPage) return 'coach';
    if (isAlignmentPage) return 'alignment';
    if (isInsightsPage) return 'insights';

    return 'other';
  }, [isInteractionsPage, isCoachPage, isAlignmentPage, isInsightsPage]);

  const tabNumber: number = useMemo((): number => {
    const availableTabs = [
      hasAlignment ? 'alignment' : null,
      hasInsights ? 'insights' : null,
      hasInteraction ? 'interaction' : null,
    ].filter(Boolean);

    if (['alignment', 'insights', 'interaction'].includes(currentPage)) {
      const index = availableTabs.indexOf(currentPage);
      return index >= 0 ? index : 0;
    }
    return 0;
  }, [hasAlignment, hasInsights, hasInteraction, currentPage]);

  const [selectedTab, setSelectedTab] = useState<number>(tabNumber);

  useEffect(() => {
    setSelectedTab(tabNumber);
  }, [tabNumber]);

  const profileData: QuickViewProfileOverview = !isLoading && !error ? data : {};

  const profileName = profileData.profile ?? flyoutParameters.profileName;
  const allTabsData: LatestActivity[] = useMemo((): LatestActivity[] => {
    const tabs: LatestActivity[] = [];
    if (hasAlignment && alignmentData?.data) {
      tabs.push(alignmentData.data);
    }
    if (hasInsights && insightsData?.data) {
      tabs.push(insightsData.data);
    }
    if (hasInteraction && interactionData?.data) {
      tabs.push(interactionData.data);
    }
    return tabs;
  }, [
    hasAlignment,
    alignmentData?.data,
    hasInsights,
    insightsData?.data,
    hasInteraction,
    interactionData?.data,
  ]);
  const overallLatestActivity: LatestActivity = useMemo((): LatestActivity => {
    if (allTabsData.length === 0) {
      return {
        latestDate: null,
        fieldTeam: '',
        territory: '',
      };
    }

    // Find the tab with the most recent date
    const latestTab = allTabsData.reduce<LatestActivity | null>((latest, current) => {
      if (!current?.latestDate) return latest;
      if (!latest?.latestDate) return current;

      const currentDate = new Date(current.latestDate);
      const latestDate = new Date(latest.latestDate);

      return currentDate > latestDate ? current : latest;
    }, null);

    if (!latestTab?.latestDate) {
      return {
        latestDate: new Date(0),
        fieldTeam: '',
        territory: '',
      };
    }

    return {
      latestDate: new Date(latestTab.latestDate),
      fieldTeam: latestTab.fieldTeam || '',
      territory: latestTab.territory || '',
    };
  }, [allTabsData]);

  const lastActivity: LatestActivity = useMemo((): LatestActivity => {
    const currentTab = allTabsData[selectedTab];

    if (!currentTab) {
      return {
        latestDate: new Date(0),
        fieldTeam: '',
        territory: '',
      };
    }

    return {
      latestDate: currentTab.latestDate ? new Date(currentTab.latestDate) : new Date(0),
      fieldTeam: currentTab.fieldTeam || '',
      territory: currentTab.territory || '',
    };
  }, [allTabsData, selectedTab]);

  //Interval calculation using overall latest activity
  const intervalSinceInteraction = useMemo(() => {
    if (!overallLatestActivity?.latestDate || overallLatestActivity.latestDate.getTime() === 0) {
      return 'N/A';
    }
    return differenceInDays(new Date(), overallLatestActivity.latestDate);
  }, [overallLatestActivity?.latestDate]);

  const navigate = (url: string, parentFilter: FilterParent, withFilters: boolean = false) => {
    if (withFilters) {
      if (!flyoutParameters.isTeamMember) {
        dispatch(
          addChartFilter({
            datasetId: DATASET_INITIAL_ID,
            chartFilter: {
              id: 'profile-quick-view',
              propertyName: 'Profile',
              propertyDisplayName: 'Profile',
              value: profileName,
            },
          })
        );
      } else {
        dispatch(
          addChartFilter({
            datasetId: DATASET_INITIAL_ID,
            chartFilter: {
              id: 'profile-quick-view',
              propertyName: 'FieldTeam',
              propertyDisplayName: 'FieldTeam',
              value: profileName,
            },
          })
        );
      }
      dispatch(applyCommonFilter({ datasetId: DATASET_INITIAL_ID }));
    }
    appNavigate(url);
    dispatch(toggleFlyout(false));
  };

  const handleSelect = (e: any) => {
    setSelectedTab(e.selected);
  };

  const renderTabHeader = (
    label: string,
    count: number,
    url: string,
    parentFilter: FilterParent
  ) => {
    return (
      <div className="tab-header">
        <div className="tab-header-left">
          <Typography.h6>
            {count} {label}
          </Typography.h6>
        </div>
        <div id="tab-right-container" className="tab-header-right">
          <DropDownList
            data={['Most Recent', 'Oldest']}
            defaultValue={'Most Recent'}
            fillMode={'outline'}
            size={'small'}
            onChange={(e) => setSort(e.value)}
            itemRender={FlyoutDropDownItem}
            rounded={'full'}
            popupSettings={{
              popupClass: 'project-selector-popup flyout-internal',
            }}
          />
          <Button
            onClick={() => navigate(url, parentFilter, true)}
            size={'small'}
            fillMode={'flat'}
            rounded={'full'}
          >
            View All <KwelloSvgIcon icon={KWELLO_ICON.FILE_FIND} />
          </Button>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <>
        <FlyoutHeader title={'Ooops'} onCloseClick={() => dispatch(toggleFlyout(false))} />
        <FlyoutBody>
          <div className="error-loading">
            <KwelloSvgIcon icon={KWELLO_ICON.WARNING_ICON} />
            <Typography.p>Server error</Typography.p>
          </div>
        </FlyoutBody>
      </>
    );
  }

  return (
    <>
      <FlyoutHeader
        title={flyoutParameters.isTeamMember ? 'Team Member Quick View' : 'Profile Quick View'}
        onCloseClick={() => dispatch(toggleFlyout(false))}
      />
      <FlyoutBody>
        {isLoading && <Spinner />}
        {!isLoading && (
          <>
            {flyoutParameters.isTeamMember && (
              <>
                <div className="flex-row team-member-heading">
                  <Avatar size={'large'} rounded="full" type={'text'}>
                    {generateAvatarInitials(profileName)}
                  </Avatar>
                  <Typography.h5>{profileName}</Typography.h5>
                </div>
                <div className="flex-row team-member-sections">
                  <div className="section-child">
                    <Typography.h6>
                      {profileData.topicInteraction ? `${profileData.topicInteraction}` : 'N/A'}
                    </Typography.h6>
                    <Typography.p>
                      Topic/
                      <br />
                      Interaction
                    </Typography.p>
                  </div>
                  <div className="section-child">
                    <Typography.h6>
                      {profileData.uniqueProfiles ? `${profileData.uniqueProfiles}` : 'N/A'}
                    </Typography.h6>
                    <Typography.p>Unique Profiles</Typography.p>
                  </div>
                  <div className="section-child">
                    <Typography.h6>
                      {lastActivity?.latestDate
                        ? formatAppDate(lastActivity.latestDate, DATE_FORMAT)
                        : 'N/A'}
                    </Typography.h6>
                    <Typography.p>Latest Activity date</Typography.p>
                  </div>
                </div>
              </>
            )}
            {!flyoutParameters.isTeamMember && (
              <>
                <Typography.h6>{profileName}</Typography.h6>
                <Typography.p>{profileData.specialty}</Typography.p>
                <div className="flex-row">
                  <div style={{ width: '50%', paddingTop: '8px' }}>
                    <Typography.h6>
                      {intervalSinceInteraction !== 'N/A'
                        ? `${intervalSinceInteraction} Days`
                        : 'N/A'}
                    </Typography.h6>
                    <Typography.p>Since last activity</Typography.p>
                  </div>
                  <div
                    style={{
                      borderRadius: 4,
                      width: '50%',
                      padding: '8px 16px',
                      background: profileData.alignmentColor1,
                    }}
                  >
                    {profileData.latestAlignment !== undefined && (
                      <div style={{ color: profileData.alignmentColor2 }}>
                        {' '}
                        <Typography.h6 style={{ color: profileData.alignmentColor2 }}>
                          {profileData.latestAlignment}
                        </Typography.h6>
                        <Typography.p>Latest alignment</Typography.p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-column" style={{ marginTop: 16 }}>
                  <div
                    className="flex-row"
                    style={{
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 11,
                    }}
                  >
                    <div>
                      <Typography.p>Latest Activity - Territory</Typography.p>
                    </div>
                    <div>
                      <Typography.p fontWeight="bold">{lastActivity.territory}</Typography.p>
                    </div>
                  </div>
                  <div
                    className="flex-row"
                    style={{
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 11,
                    }}
                  >
                    <div>
                      <Typography.p>Latest Activity - Team Member</Typography.p>
                    </div>
                    <div className="flex-row">
                      {lastActivity.fieldTeam && (
                        <Avatar rounded="full" type={'text'}>
                          {generateAvatarInitials(lastActivity.fieldTeam)}
                        </Avatar>
                      )}
                      <Typography.p>{lastActivity.fieldTeam}</Typography.p>
                    </div>
                  </div>
                  <div
                    className="flex-row"
                    style={{
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 11,
                    }}
                  >
                    <div>
                      <Typography.p>Latest Activity - Date</Typography.p>
                    </div>
                    <div>
                      <Typography.p fontWeight="bold">
                        {lastActivity?.latestDate
                          ? formatAppDate(new Date(lastActivity.latestDate), DATE_FORMAT)
                          : 'N/A'}
                      </Typography.p>
                    </div>
                  </div>
                </div>
              </>
            )}
            <TabStrip className="profileFlyoutTabs" selected={selectedTab} onSelect={handleSelect}>
              {hasAlignment && (
                <TabStripTab
                  title={
                    'Alignment ' +
                    (alignmentData?.data?.total && alignmentData?.data?.total > 0
                      ? ' (' + alignmentData?.data?.total + ') '
                      : '')
                  }
                >
                  <AlignmentTab
                    HeaderComponent={renderTabHeader(
                      'Alignment(s)',
                      alignmentData?.data?.total,
                      ALIGNMENT_URL,
                      FilterParent.ALIGNMENT
                    )}
                    navigate={navigate}
                    isTeamMember={flyoutParameters.isTeamMember}
                    {...alignmentData}
                  />
                </TabStripTab>
              )}
              {hasInsights && (
                <TabStripTab
                  title={
                    'Insights ' +
                    (insightsData?.data?.total && insightsData?.data?.total > 0
                      ? ' (' + insightsData?.data?.total + ') '
                      : '')
                  }
                >
                  <InsightsTab
                    HeaderComponent={renderTabHeader(
                      'Insight(s)',
                      insightsData?.data?.total,
                      INSIGHTS_URL,
                      FilterParent.SURVEY
                    )}
                    navigate={navigate}
                    isTeamMember={flyoutParameters.isTeamMember}
                    {...insightsData}
                  />
                </TabStripTab>
              )}
              {hasInteraction && (
                <TabStripTab
                  title={
                    'Interactions ' +
                    (interactionData?.data?.total && interactionData?.data?.total > 0
                      ? ' (' + interactionData?.data?.total + ') '
                      : '')
                  }
                >
                  <InteractionTab
                    HeaderComponent={renderTabHeader(
                      'Interaction(s)',
                      interactionData?.data?.total,
                      INTERRACTION_URL,
                      FilterParent.INTERACTION
                    )}
                    navigate={navigate}
                    isTeamMember={flyoutParameters.isTeamMember}
                    {...interactionData}
                  />
                </TabStripTab>
              )}
            </TabStrip>
          </>
        )}
      </FlyoutBody>
      {flyoutParameters.isTeamMember === true && (
        <FlyoutFooter>
          <Button onClick={() => dispatch(toggleFlyout(false))} size={'small'} fillMode={'flat'}>
            Close
          </Button>

          {hasTeamDetailPage && (
            <Button
              onClick={() => {
                setSessionItem(TEAM_MEMBER_SESSION_KEY, {
                  id: flyoutParameters.profileId,
                  displayName: flyoutParameters.profileName,
                });
                appNavigate(
                  COACH_ROUTES.team_detail_interaction_kpis.replace(
                    ':id',
                    flyoutParameters.profileId
                  )
                );
                dispatch(toggleFlyout(false));
              }}
              fillMode="solid"
              themeColor="primary"
            >
              View Team Member Details <KwelloSvgIcon icon={KWELLO_ICON.OPEN_ARROW_ICON} />
            </Button>
          )}
        </FlyoutFooter>
      )}
    </>
  );
}
